import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchApplicationDetails, API_BASE_URL } from '../../../utils/apiUtils';
import styled from 'styled-components';
import { saveFormData } from '../../../utils/apiUtils';
import FilePreviewModal from '../../common/FilePreviewModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';
import SignatureCanvas from 'react-signature-canvas';

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545;
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000;
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px;
  background-color: white;
  padding: 50px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
  box-sizing: border-box;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #212529;
  font-size: 13px;
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const FormContentContainer = styled.div`
  /* Form content styling */
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const SaveDraftButton = styled.button`
  background-color: #ffc107;
  color: #212529;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px;
  margin-right: 10px;
  display: inline-block;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px;
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9;
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

// Form specific styled components
const FormTitle = styled.h2`
  font-size: 1.3em;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
  color: #333;
`;

const UndertakingContainer = styled.div`
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

const UndertakingText = styled.p`
  text-align: justify;
  font-size: 0.95em;
  line-height: 1.8;
  color: #333;
  margin-bottom: 15px;
`;

const FormFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 30px;
`;

const FormField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormLabel = styled.label`
  font-weight: 500;
  color: #333;
  min-width: 100px;
`;

const FormInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
  
  &:focus {
    outline: none;
    border-color: #6cb288;
  }
`;

const SignatureContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 10px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.label`
  display: inline-block;
  padding: 8px 16px;
  background-color: #6cb288;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #4d9e70;
  }
`;

const TouchpadButton = styled.button`
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #0069d9;
  }
`;

const SignaturePreview = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  min-width: 200px;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f9f9f9;
`;

const NotesContainer = styled.div`
  margin-top: 40px;
  padding: 20px;
  background-color: #f0f8ff;
  border: 1px solid #cce5ff;
  border-radius: 4px;
`;

const NotesTitle = styled.h3`
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 10px;
  color: #004085;
`;

const NotesText = styled.p`
  font-size: 0.9em;
  color: #004085;
  margin-bottom: 20px;
`;

const ContactTitle = styled.h4`
  font-size: 1em;
  font-weight: 600;
  margin-top: 20px;
  margin-bottom: 15px;
  color: #333;
`;

const ContactSection = styled.div`
  margin-bottom: 15px;
`;

const ContactArea = styled.h5`
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
  font-size: 0.95em;
`;

const ContactDetails = styled.p`
  font-size: 0.85em;
  color: #555;
  line-height: 1.6;
  margin-left: 10px;
`;

// Styled components for Signature Modal
const SignatureModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050; // Higher than other elements
`;

const SignatureModalContent = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: auto;
  max-width: 90vw; // Ensure it fits on smaller screens
`;

const SignaturePadWrapper = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%; // Default width
  height: 200px; // Default height
  canvas {
    width: 100% !important; // Override inline styles of canvas if necessary
    height: 100% !important;
  }
`;

const SignatureModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
`;

const SignatureButton = styled.button`
  padding: 8px 15px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s ease;

  &.save {
    background-color: #28a745; // Green for save
    color: white;
    &:hover { background-color: #218838; }
  }

  &.clear {
    background-color: #ffc107; // Yellow for clear
    color: #212529;
    &:hover { background-color: #e0a800; }
  }

  &.cancel {
    background-color: #6c757d; // Gray for cancel
    color: white;
    &:hover { background-color: #5a6268; }
  }
`;

interface FormData {
  applicationNoOfficeUse: string;
  place: string;
  date: string;
  signatureFile: File | string | null;
  errors?: {
    place?: string;
    date?: string;
    signatureFile?: string;
  };
}

interface ApplicationFormPage7Props {
  formData?: any;
  isViewMode?: boolean;
}

const initialState: FormData = {
  applicationNoOfficeUse: '',
  place: '',
  date: '',
  signatureFile: null,
  errors: {}
};

const ApplicationFormPage7: React.FC<ApplicationFormPage7Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [applicationId, setApplicationId] = useState<string>('');
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  
  // Progress bar state
  const [formProgress, setFormProgress] = useState<number>(0);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const signaturePadRef = useRef<SignatureCanvas>(null);
  const [signatureImageUrl, setSignatureImageUrl] = useState<string | null>(null);
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(propFormData || { ...initialState });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  
  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // PDF Generation State
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    if (propFormData?.applicationNoOfficeUse) {
      setApplicationId(propFormData.applicationNoOfficeUse);
      if (typeof propFormData.signatureFile === 'string' && propFormData.signatureFile) {
        const sigPath = propFormData.signatureFile.startsWith('/uploads/') ? propFormData.signatureFile : `/uploads/${propFormData.signatureFile}`;
        setSignatureImageUrl(`${API_BASE_URL}${sigPath}`);
      }
      return;
    }

    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[ApplicationFormPage7 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[ApplicationFormPage7 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true;
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  }, [location.state, propFormData]);

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    if (propFormData) return; // Skip loading if formData is passed as a prop

    const loadDraftData = async () => {
      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[ApplicationFormPage7 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          const appData = await fetchApplicationDetails(applicationId);
          if (appData && appData.formData) {
            const loadedFormData = {
              ...initialState,
              ...appData.formData,
            };
            setFormData(loadedFormData);
            if (typeof loadedFormData.signatureFile === 'string' && loadedFormData.signatureFile) {
              const sigPath = loadedFormData.signatureFile.startsWith('/uploads/') ? loadedFormData.signatureFile : `/uploads/${loadedFormData.signatureFile}`;
              setSignatureImageUrl(`${API_BASE_URL}${sigPath}`);
            } else {
              setSignatureImageUrl(null);
            }
            console.log(`[ApplicationFormPage7 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[ApplicationFormPage7 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[ApplicationFormPage7 LoadDraft] Error loading draft data for ${applicationId}:`, error);
        }
      }
    };

    loadDraftData();
  }, [applicationId, isViewMode, propFormData]);

  // Helper function to convert Data URL to File object
  const dataURLtoFile = (dataurl: string, filename: string): File | null => {
    try {
      const arr = dataurl.split(',');
      if (arr.length < 2) return null;
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch || mimeMatch.length < 2) return null;
      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (error) {
      console.error('Error converting data URL to file:', error);
      return null;
    }
  };

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleOpenFilePreview = (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    console.log('[handleOpenFilePreview] Current API_BASE_URL:', API_BASE_URL);

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      console.log('[handleOpenFilePreview] File is a string:', file);

      // Determine backend origin consistently using REACT_APP_API_URL if absolute; otherwise fallback to current origin
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const urlObject = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${urlObject.protocol}//${urlObject.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      } else {
        // Relative API URL - check if we're in development
        const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        if (isDevelopment && window.location.port === '3000') {
          serverBaseForFiles = `${window.location.protocol}//${window.location.hostname}:5000`;
        }
      }

      // Normalize to a path
      let normalizedPath = file;
      if (file.includes('://')) {
        try { normalizedPath = new URL(file).pathname; } catch { normalizedPath = file; }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.includes('/')
          ? `/${normalizedPath}`
          : `/uploads/${normalizedPath}`;
      }

      url = `${serverBaseForFiles}${normalizedPath}`;
      name = normalizedPath.split('/').pop() || defaultFileName;

      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (lower.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = lower.split('.').pop();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream';
      }
      console.log(`[handleOpenFilePreview] Constructed URL: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      console.log('[handleOpenFilePreview] File is a File object:', file);
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] File object - Constructed URL:', url, 'Name:', name, 'Type:', type);
    }

    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    try {
      if (formRef.current) {
        const buttonsToHide = formRef.current.querySelectorAll('button');
        buttonsToHide.forEach(btn => btn.style.visibility = 'hidden');

        const canvas = await html2canvas(formRef.current, {
          scale: 2, // Higher resolution for better quality
          useCORS: true, // Enable CORS for images if any
        });

        buttonsToHide.forEach(btn => btn.style.visibility = 'visible');

        const imgData = canvas.toDataURL('image/jpeg', 0.5); // Use aggressive JPEG compression
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'mm',
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const margin = 10; // 10mm margins
        const imageWidth = pdfWidth - margin * 2;
        const imageHeight = (canvas.height * imageWidth) / canvas.width;

        pdf.addImage(imgData, 'JPEG', margin, margin, imageWidth, imageHeight);

        // Simulate compression process for visual feedback
        for (let i = 0; i <= 100; i += 10) {
          setCompressionProgress(i);
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        pdf.save(`application_form_page7_${applicationId || "draft"}.pdf`);
      } else {
        console.error('Form content is not available for PDF export.');
        alert('Form content is not available for PDF export.');
      }
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert(`Error generating PDF for Page 7. Please check the console for details. Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  // Function to save data to database
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      return;
    }

    if (!isOnline) {
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.');
      return;
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      // Create a plain JavaScript object to hold the form data
      const formDataObj: Record<string, any> = {};

      // First, ensure all expected fields exist with at least empty string values
      // This prevents issues with undefined fields in the database
      const expectedFields = [
        'applicationNoOfficeUse',
        'declarationName',
        'declarationDate',
        'signatureFile',
        'declarationAgree',
        // Add any other fields that should be present in Page 7
      ];

      // Initialize all expected fields with empty strings
      expectedFields.forEach(field => {
        formDataObj[field] = '';
      });

      // Copy non-file fields from data to the object
      Object.keys(data).forEach(key => {
        if (key === 'errors') return; // Don't send client-side errors object

        const value = data[key as keyof typeof data];
        
        if (key === 'signatureFile') {
          if (typeof value === 'string' && value.trim() !== '') {
            // If it's a string (e.g., a file ID/path from a previous save), include it
            formDataObj[key] = value;
          }
          // File objects will be handled separately through file upload endpoints
        } else if (value !== null && value !== undefined) {
          // Include all other fields
          formDataObj[key] = value;
        }
        // Fields not in data but in expectedFields already have empty string values
      });

      // Ensure applicationNoOfficeUse is present
      if (!formDataObj.applicationNoOfficeUse && idToUse) {
        formDataObj.applicationNoOfficeUse = idToUse;
      }

      console.log('[SaveToDB] Data to send:', formDataObj);
      
      // Call saveFormData with the plain object (not FormData)
      // The saveFormData function already wraps the data in { formData: data, isDraft }
      const response = await saveFormData(idToUse, formDataObj, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false;
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline]);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[ApplicationFormPage7 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId);
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline]);

  const handleFillDummyData = () => {
    console.log('[ApplicationFormPage7 FillDummyData] Filling form with dummy data.');
    setFormData(prevData => ({
      ...prevData,
      applicationNoOfficeUse: prevData.applicationNoOfficeUse || applicationId || '',
      place: 'Pune',
      date: new Date().toISOString().split('T')[0],
      errors: {},
    }));
    setSaveMessage('Dummy data filled. Save to persist.');
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
    }, 3000);
  };

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      if (applicationId) {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}`);
      }
    }, 1000);
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  const handleSignatureFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const updatedFormData = { ...formData, signatureFile: file };
      setFormData(updatedFormData);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setSignatureImageUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setSignatureImageUrl(null); // Clear preview for non-image files like PDF
      }

      
    } else {
      setSignatureImageUrl(null); // Clear preview if no file is selected
      // Optionally, also clear formData.signatureFile if needed, e.g., setFormData({ ...formData, signatureFile: null });
    }
  };

  const handleTouchpadSign = () => {
    setIsSignatureModalOpen(true);
  };

  const handleClearSignature = () => {
    signaturePadRef.current?.clear();
    setSignatureImageUrl(null); // Clear the image preview as well
  };

  const handleSaveSignature = () => {
    if (signaturePadRef.current) {
      if (signaturePadRef.current.isEmpty()) {
        alert('Please provide a signature first.');
        return;
      }
      const signatureDataUrl = signaturePadRef.current.toDataURL('image/png');
      setSignatureImageUrl(signatureDataUrl); // Set preview from touchpad
      const signatureFile = dataURLtoFile(signatureDataUrl, `signature-${applicationId || Date.now()}.png`);
      
      if (signatureFile) {
        const updatedFormData = { ...formData, signatureFile: signatureFile };
        setFormData(updatedFormData);
        setShowSaveIndicator(true);
        setSaveMessage('Saving signature...');
        debouncedSave(updatedFormData);
      }
      setIsSignatureModalOpen(false);
    }
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };

  const handleNextButtonClick = async () => {
    // Save draft before navigation
    if (applicationId && isOnline) {
      console.log('[Navigation Page7] Saving draft before navigating');
      await saveToDatabaseCallback({ ...formData, applicationNoOfficeUse: applicationId }, true, applicationId);
    }
    if (applicationId && applicationId !== formData.applicationNoOfficeUse) {
      setFormData(prev => ({
        ...prev,
        applicationNoOfficeUse: applicationId
      }));
    }
    navigate('/form/page8');
  };

  // Calculate form progress whenever form data changes
  useEffect(() => {
    const totalFields = 3; // place, date, signature
    let filledFields = 0;
    
    if (formData.place && formData.place.trim() !== '') filledFields++;
    if (formData.date && formData.date.trim() !== '') filledFields++;
    if (formData.signatureFile) filledFields++;

    const progressPercentage = totalFields > 0 ? (filledFields / totalFields) * 100 : 0;
    setFormProgress(progressPercentage);
  }, [formData]);

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const getSignatureFileName = (): string => {
    if (!formData.signatureFile) return 'No signature uploaded';
    if (typeof formData.signatureFile === 'string') return formData.signatureFile;
    return formData.signatureFile.name;
  };

  return (
    <>
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel>Page 7/14 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      
      <FloatingBackButton onClick={() => navigate('/form/page6')} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      
      <FormPageNavigator currentPage={7} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} />
      
      <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page 8/14 →
        </NextPageButton>
      </div>
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            <FormTitle>Undertaking & Declaration</FormTitle>
            
            <UndertakingContainer>
              <UndertakingText>
                I undertake to produce Originals of all the attachments enclosed with this application at the time of interview, if called and as and when required. In the event if fail to produce the originals, my offer of land for long term lease and my application for Dealership will be disqualified. I am fully aware that Maharashtra Natural Gas Limited, under its policy will not appoint me as their Dealer if I am employed. I shall not be resigning from the service and produce proof of acceptance of my resignation by my employer to MNGL before issuance of Letter of Award by MNGL.
              </UndertakingText>
              <UndertakingText>
                I further, I undertake that I will be required to deposit an interest free Security Deposit & avail the security deposit as per the details in the Advertisement against which this application is being made.
              </UndertakingText>
              <UndertakingText>
                I hereby certify that the information given above is true to the best of my knowledge and belief. Any wrong information / suppression of facts will disqualify me from being considered for the Dealership.
              </UndertakingText>
            </UndertakingContainer>

            <FormFieldsContainer>
              <FormField>
                <FormLabel>Place:</FormLabel>
                <FormInput
                  type="text"
                  name="place"
                  value={formData.place}
                  onChange={handleChange}
                  placeholder="Enter place"
                />
              </FormField>

              <FormField>
                <FormLabel>Date:</FormLabel>
                <FormInput
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                />
              </FormField>

              <FormField>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <FormLabel style={{ marginBottom: 0 }}>Signature:</FormLabel>
                  {signatureImageUrl && (
                    <img
                      src={signatureImageUrl}
                      alt="Signature Preview"
                      style={{ maxHeight: '60px', maxWidth: '180px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  )}
                </div>
                <SignatureContainer>
                  <FileUploadButton htmlFor="signatureFile">
                    Upload Signature
                    <FileInput
                      type="file"
                      id="signatureFile"
                      accept="image/*,.pdf"
                      onChange={handleSignatureFileChange}
                    />
                  </FileUploadButton>
                  <span style={{ color: '#666' }}>or</span>
                  <TouchpadButton onClick={handleTouchpadSign}>
                    Sign using Touchpad
                  </TouchpadButton>
                </SignatureContainer>
              </FormField>

              {formData.signatureFile && (
                <SignaturePreview>
                  <span style={{ fontSize: '0.9em', color: '#666' }}>
                    {getSignatureFileName()}
                  </span>
                  <button 
                    onClick={() => handleOpenFilePreview(formData.signatureFile, 'Signature')} 
                    style={{ marginLeft: '10px', fontSize: '0.8em' }}
                  >
                    Preview
                  </button>
                </SignaturePreview>
              )}
            </FormFieldsContainer>

            <NotesContainer>
              <NotesTitle>Note:</NotesTitle>
              <NotesText>
                Application with all the information and documents attached should be submitted in person or by hand at the following address. For further details please visit www.mngl.in
              </NotesText>

              <ContactTitle>Contact Address:</ContactTitle>
              
              <ContactSection>
                <ContactArea>1. Pune Geographical Area</ContactArea>
                <ContactDetails>
                  Maharashtra Natural Gas Limited, Plot no. 27, Narveer Tanajiwadi PMPML bus depot, Commercial building, 1st floor, Shivajinagar, Pune-411005. Ph: 020-25611000
                </ContactDetails>
              </ContactSection>

              <ContactSection>
                <ContactArea>2. Nashik-Dhule Geographical Area</ContactArea>
                <ContactDetails>
                  Maharashtra Natural Gas Limited, at F-12, Mohanagar Gardens, Gate No-1, Jay Bhavani road, Behind St. Xavier's high school, Nasik road, Nasik-422102. Ph: 0253-2410004
                </ContactDetails>
              </ContactSection>

              <ContactSection>
                <ContactArea>3. Sindhudurg Geographical Area</ContactArea>
                <ContactDetails>
                  Maharashtra Natural Gas Limited at Arunodaya Bunglow, House no-795-182, A/P-Kubalwadi Maneri, Tal-Dodamarg, Dist-Sindhudurg –416512 Ph: 7588059055
                </ContactDetails>
              </ContactSection>

              <ContactSection>
                <ContactArea>4. Ramanagara Geographical Area</ContactArea>
                <ContactDetails>
                  Maharashtra Natural Gas Limited, B. M. Road, Opp. H. P. Petrol Pump, Mysuru National Highway, Ramanagara-562159, Karnataka, Ph: 6364901200
                </ContactDetails>
              </ContactSection>
            </NotesContainer>
          </FormContentContainer>
          <PageNumber>7</PageNumber>
        </A4PageContainer>
        
        <FilePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fileUrl={modalFileUrl}
          fileName={modalFileName}
          fileType={modalFileType}
        />
      </PageWrapper>
      
      {/* Empty ActionButtonsContainer to maintain structure */}
      <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000 }}>
        {/* Next Page button moved near FormPageNavigator */}
      </ActionButtonsContainer>
      
      {/* Session Timer */}
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Home Button - Above Download PDF */}
      <button
        onClick={() => navigate('/user-dashboard')}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '95px',
          bottom: isViewMode ? '100px' : '175px',
          zIndex: 1000,
          width: '145px',
          height: '45px',
          borderRadius: '50px',
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '13px',
          fontWeight: '600',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
          transition: 'all 0.2s ease'
        }}
      >
        🏠 Dashboard
      </button>

      {!isViewMode && (
        <>
          <PayNowButton 
            onClick={handleDownloadPdf} 
            disabled={!isOnline || isViewMode}
            style={{ position: 'fixed', left: '95px', bottom: '105px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
          >
            DOWNLOAD PDF
          </PayNowButton>
          
          <SaveDraftButton 
            onClick={() => saveToDatabaseCallback(formData, true, applicationId)} 
            disabled={isSaving || !applicationId || !isOnline}
            style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.2px' }}
          >
            {isSaving && saveMessage.startsWith('Saving Draft') ? 'Saving...' : 'SAVE DRAFT'}
          </SaveDraftButton>
        </>
      )}
      
      {isViewMode && (
        <PayNowButton 
          onClick={handleDownloadPdf} 
          disabled={!isOnline}
          style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
        > 
          DOWNLOAD PDF
        </PayNowButton>
      )}
      
      {isSignatureModalOpen && (
        <SignatureModalOverlay>
          <SignatureModalContent>
            <h4>Provide Signature</h4>
            <SignaturePadWrapper>
              <SignatureCanvas
                ref={signaturePadRef}
                penColor='black'
                canvasProps={{
                  style: { width: '100%', height: '100%', border: '1px solid #dee2e6' }
                }}
              />
            </SignaturePadWrapper>
            <SignatureModalActions>
              <SignatureButton className="clear" onClick={handleClearSignature}>Clear</SignatureButton>
              <SignatureButton className="save" onClick={handleSaveSignature}>Save Signature</SignatureButton>
              <SignatureButton className="cancel" onClick={() => setIsSignatureModalOpen(false)}>Cancel</SignatureButton>
            </SignatureModalActions>
          </SignatureModalContent>
        </SignatureModalOverlay>
      )}
      
      {isPdfGenerating && (
        <ModalOverlay>
          <ModalContent>
            <h3>Generating PDF</h3>
            <p>Please wait... Generating PDF</p>
            <ProgressBarContainerModal>
              <ProgressBarFillModal progress={compressionProgress} />
            </ProgressBarContainerModal>
            <ProgressText>Compression: {compressionProgress}%</ProgressText>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default ApplicationFormPage7;

// Helper to make all props of a styled component optional for the 'as' prop or other scenarios
type Optional<T> = { [P in keyof T]+?: T[P] };

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
`;

const ProgressBarContainerModal = styled.div`
  width: 100%;
  height: 20px;
  background-color: #e9ecef;
  border-radius: 10px;
  margin-top: 20px;
  overflow: hidden;
`;

const ProgressBarFillModal = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => props.progress}%; // Dynamic width based on progress
  background-color: #6cb288;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled.p`
  margin-top: 10px;
  font-size: 0.9em;
  color: #555;
`;